import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, MoreHorizontal, RotateCcw, Search } from 'lucide-react';

export interface ColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
}

interface Prefs { order: string[]; visible: string[] }

// Visibilità e ordine colonne persistiti in localStorage per pagina (storageKey).
export function useColumnPrefs(storageKey: string, defs: ColumnDef[]) {
  const defaults = (): Prefs => ({
    order: defs.map(d => d.key),
    visible: defs.filter(d => d.defaultVisible).map(d => d.key),
  });
  const [prefs, setPrefs] = useState<Prefs>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Formato legacy: solo array delle colonne visibili.
        if (Array.isArray(parsed)) return { order: defs.map(d => d.key), visible: parsed };
        if (parsed && Array.isArray(parsed.order) && Array.isArray(parsed.visible)) return parsed;
      }
    } catch { /* valore corrotto: si riparte dai default */ }
    return defaults();
  });

  function save(next: Prefs) {
    setPrefs(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  }

  // Colonne nell'ordine salvato; eventuali colonne nuove (non ancora in ordine) vanno in coda.
  const orderedDefs = [
    ...prefs.order.map(k => defs.find(d => d.key === k)).filter((d): d is ColumnDef => !!d),
    ...defs.filter(d => !prefs.order.includes(d.key)),
  ];
  const visible = new Set(prefs.visible);

  function setVisible(next: Set<string>) {
    save({ ...prefs, visible: [...next] });
  }
  function moveColumn(fromKey: string, toKey: string) {
    const order = orderedDefs.map(d => d.key);
    const from = order.indexOf(fromKey);
    const to = order.indexOf(toKey);
    if (from < 0 || to < 0 || from === to) return;
    order.splice(to, 0, ...order.splice(from, 1));
    save({ ...prefs, order });
  }
  function reset() {
    localStorage.removeItem(storageKey);
    setPrefs(defaults());
  }
  return { orderedDefs, visible, setVisible, moveColumn, reset };
}

export default function ColumnPicker({ defs, visible, onChange, onReset, onMove }: {
  defs: ColumnDef[];
  visible: Set<string>;
  onChange: (next: Set<string>) => void;
  onReset: () => void;
  onMove: (fromKey: string, toKey: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  // Posizione fissa calcolata dal bottone: il pannello sfugge così al clipping di overflow-x-auto.
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function openPanel() {
    const r = btnRef.current!.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = defs.filter(d => d.label.toLowerCase().includes(search.toLowerCase()));
  const allChecked = defs.every(d => visible.has(d.key));

  function toggle(key: string) {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  }

  return (
    <div className="inline-block" ref={ref}>
      <button ref={btnRef} onClick={() => (open ? setOpen(false) : openPanel())} title="Show Columns"
        className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 align-middle">
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-xl text-left normal-case tracking-normal font-normal">
          <div className="px-3 pt-3 pb-2 border-b border-gray-100">
            <div className="font-semibold text-gray-900 text-sm mb-2">Show Columns</div>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search keywords"
                className="input-field w-full pl-7 text-sm" />
            </div>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" checked={allChecked}
                ref={el => { if (el) el.indeterminate = !allChecked && visible.size > 0; }}
                onChange={() => onChange(allChecked ? new Set() : new Set(defs.map(d => d.key)))} />
              Select All
            </label>
            <button onClick={onReset} className="text-primary-700 text-sm hover:underline flex items-center gap-1">
              <RotateCcw size={12} /> Reset
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.map(d => (
              <label key={d.key}
                draggable={search === ''}
                onDragStart={() => setDragKey(d.key)}
                onDragOver={e => { e.preventDefault(); setOverKey(d.key); }}
                onDrop={() => { if (dragKey) onMove(dragKey, d.key); setDragKey(null); setOverKey(null); }}
                onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                className={`flex items-center gap-2 px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-50 cursor-pointer ${
                  dragKey && overKey === d.key && dragKey !== d.key ? 'border-t-2 border-primary-400' : ''}`}>
                <GripVertical size={12} className={`shrink-0 ${search === '' ? 'text-gray-300 cursor-grab' : 'text-gray-200'}`} />
                <input type="checkbox" checked={visible.has(d.key)} onChange={() => toggle(d.key)} />
                {d.label}
              </label>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No column matches</div>}
          </div>
          <div className="px-3 py-1.5 border-t border-gray-100 text-[11px] text-gray-400">
            Drag rows to reorder columns
          </div>
        </div>
      )}
    </div>
  );
}

// Ordinamento per colonna (2026-10-06): un click sull'intestazione ordina in modo crescente, un
// secondo click sulla stessa colonna in modo decrescente, un terzo di nuovo crescente. Avviene
// lato client sulle righe caricate (le griglie caricano già tutto ciò che il filtro ammette).
// `getValue` dice per ogni colonna quale valore confrontare: numeri come numeri, testo senza
// distinzione di maiuscole, mancanti (null/undefined/'') sempre in fondo in entrambi i versi.
export type SortDir = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDir }
export type SortValue = string | number | null | undefined;

export function useColumnSort<T>(rows: T[], getValue: (row: T, key: string) => SortValue) {
  const [sort, setSort] = useState<SortState | null>(null);
  function toggleSort(key: string) {
    setSort(prev => (prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const direction = sort.dir === 'asc' ? 1 : -1;
    return rows.map((row, index) => ({ row, index, value: getValue(row, sort.key) }))
      .sort((a, b) => {
        const aMissing = a.value === null || a.value === undefined || a.value === '';
        const bMissing = b.value === null || b.value === undefined || b.value === '';
        if (aMissing || bMissing) return aMissing === bMissing ? a.index - b.index : aMissing ? 1 : -1;
        let cmp: number;
        if (typeof a.value === 'number' && typeof b.value === 'number') cmp = a.value - b.value;
        else cmp = String(a.value).localeCompare(String(b.value), undefined, { sensitivity: 'base', numeric: true });
        // A parità di valore l'ordine di arrivo resta stabile.
        return cmp === 0 ? a.index - b.index : cmp * direction;
      })
      .map(x => x.row);
  }, [rows, sort, getValue]);
  return { sort, toggleSort, sorted };
}

// Intestazione <th> trascinabile: usare al posto di <th> in qualsiasi griglia per permettere
// il riordino colonne trascinando direttamente l'header, senza passare dal pannello ColumnPicker.
// La chiave della colonna trascinata viaggia nel dataTransfer nativo, quindi ogni <th> resta
// stateless e la stessa intestazione può essere sorgente o destinazione del drop.
// Con `onSort` l'intestazione è anche cliccabile per ordinare (vedi useColumnSort): un
// trascinamento non genera click, quindi le due cose convivono sullo stesso elemento.
export function DraggableTh({ colKey, onMove, className, children, sort, onSort }: {
  colKey: string;
  onMove: (fromKey: string, toKey: string) => void;
  className?: string;
  children: React.ReactNode;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}) {
  const [isOver, setIsOver] = useState(false);
  const active = sort?.key === colKey;

  return (
    <th
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', colKey); }}
      onDragOver={e => { e.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={e => {
        e.preventDefault();
        const fromKey = e.dataTransfer.getData('text/plain');
        if (fromKey) onMove(fromKey, colKey);
        setIsOver(false);
      }}
      onClick={onSort ? () => onSort(colKey) : undefined}
      title={onSort ? (active ? `Sorted ${sort!.dir === 'asc' ? 'ascending' : 'descending'} — click to reverse` : 'Click to sort') : undefined}
      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={`select-none ${onSort ? 'cursor-pointer hover:text-gray-900' : 'cursor-grab'} ${isOver ? 'border-l-2 border-primary-400' : ''} ${active ? 'text-primary-700' : ''} ${className ?? ''}`}
    >
      <span className="inline-flex items-center gap-1">
        <GripVertical size={11} className="shrink-0 text-gray-300" />
        {children}
        {active && (sort!.dir === 'asc' ? <ArrowUp size={11} className="shrink-0" /> : <ArrowDown size={11} className="shrink-0" />)}
      </span>
    </th>
  );
}

